import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../apiBase';
import * as AuthContext from '../context/AuthContext';

export const WHATSAPP_CAPABILITY_EVENT = 'aurax:whatsapp-capability-changed';

const AVAILABLE_FOR_OFFICE = Object.freeze({
  loading: false,
  configured: true,
  connected: true,
  automationAvailable: true,
  reason: null,
  integrationRequired: false,
});

const INITIAL_RESELLER_STATE = Object.freeze({
  loading: true,
  configured: false,
  connected: false,
  automationAvailable: false,
  reason: 'checking',
  integrationRequired: false,
});

let cached = null;
let cachedAt = 0;
let inflight = null;
const useOptionalAuth = typeof AuthContext.useAuth === 'function' ? AuthContext.useAuth : () => ({});

export function normalizeWhatsAppCapability(data = {}) {
  const automationAvailable =
    typeof data.automation_available === 'boolean'
      ? data.automation_available
      : data.needs_setup === false;
  const connected = typeof data.connected === 'boolean' ? data.connected : automationAvailable;
  const configured = typeof data.configured === 'boolean' ? data.configured : connected;
  return {
    loading: false,
    configured,
    connected,
    automationAvailable,
    reason: data.reason || (automationAvailable ? null : configured ? 'disconnected' : 'not_configured'),
    integrationRequired: !!data.integration_required,
  };
}

async function loadCapability(force = false) {
  const fresh = cached && Date.now() - cachedAt < 15000;
  if (!force && fresh) return cached;
  if (!force && inflight) return inflight;
  inflight = axios
    .get(`${API_BASE_URL}/api/whatsapp/needs-setup`, { withCredentials: true })
    .then(({ data }) => {
      cached = normalizeWhatsAppCapability(data);
      cachedAt = Date.now();
      return cached;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function notifyWhatsAppCapabilityChanged() {
  cached = null;
  cachedAt = 0;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WHATSAPP_CAPABILITY_EVENT));
  }
}

export function useWhatsAppCapability() {
  const { user } = useOptionalAuth();
  const isReseller = String(user?.role || '') === 'reseller';
  const [state, setState] = useState(isReseller ? INITIAL_RESELLER_STATE : AVAILABLE_FOR_OFFICE);

  const refresh = useCallback(async (force = true) => {
    if (!isReseller) {
      setState(AVAILABLE_FOR_OFFICE);
      return AVAILABLE_FOR_OFFICE;
    }
    try {
      const next = await loadCapability(force);
      setState(next);
      return next;
    } catch {
      const unavailable = {
        ...INITIAL_RESELLER_STATE,
        loading: false,
        reason: 'unavailable',
      };
      setState(unavailable);
      return unavailable;
    }
  }, [isReseller]);

  useEffect(() => {
    let active = true;
    if (!isReseller) {
      setState(AVAILABLE_FOR_OFFICE);
      return undefined;
    }
    loadCapability(false)
      .then((next) => { if (active) setState(next); })
      .catch(() => {
        if (active) setState({ ...INITIAL_RESELLER_STATE, loading: false, reason: 'unavailable' });
      });
    const onChanged = () => { if (active) void refresh(true); };
    window.addEventListener(WHATSAPP_CAPABILITY_EVENT, onChanged);
    return () => {
      active = false;
      window.removeEventListener(WHATSAPP_CAPABILITY_EVENT, onChanged);
    };
  }, [isReseller, refresh, user?.id]);

  return { ...state, refresh };
}
